import React, { useState, useContext, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import TableGrid from "../../../components/UI/TableGrid";
import Icons from "../../../components/UI/Icons";
import FormInputs from "../../../components/UI/FormInputs";
import dayjs from "dayjs";
import EmployeeService from "../../../services/EmployeeService";
import LoaderIcon from "../../../components/UI/Icons/LoaderIcon";
import WithRoutify from "../../../hoc/WithRoutify";
import WithReduxState from "../../../hoc/wReduxState";
const SlashNote = (props)=>{
    const {patientDetails,selectedTab,Loader}=props;
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [noteDate, setNoteDate] = useState("");
    const [noteText, setNoteText] = useState("");
    const [dateError, setDateError] = useState("");
    const [textError, setTextError] = useState("");
    const [slashNotes, setSlashNotes] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [editingNoteId, setEditingNoteId] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [pagination, setPagination] = useState({
        page: 1,
        perPage: 25,
        totalRecords: 0,
        lastPage: 1,
        from: 0,
        to: 0
    });
    const [sortBy, setSortBy] = useState('note_date');
    const [sortOrder, setSortOrder] = useState('desc');
    const tableHeaders = [
        { label: "Discussion Date", key: "note_date" },
        { label: "Discussion Note", key: "note_text" },
        { label: "Actions", key: "actions", type: "Actions" }
    ];

    useEffect(() => {
        if (patientDetails?.employee_id) {
            fetchSlashNotes();
        }
    }, [patientDetails?.employee_id, pagination.page, pagination.perPage, sortBy, sortOrder]);

    const fetchSlashNotes = async () => {
        if (!patientDetails?.employee_id) return;
        Loader(true);
        try {
            const queryParams = {
                page: pagination.page,
                perPage: pagination.perPage,
                sort_by: sortBy,
                sort_order: sortOrder
            };
            const response = await EmployeeService.getSlashNotes(patientDetails.employee_id, queryParams);
            if (response) {
                let paginatedData = null;
                 // Check if response is the paginator object directly (has current_page, data, total, etc.)
                if (response.current_page !== undefined && response.data !== undefined) {
                    paginatedData = response;
                } 
                // Check if response.data is the paginator (nested structure)
                else if (response.data && response.data.current_page !== undefined && response.data.data !== undefined) {
                    paginatedData = response.data;
                }
            
                if (paginatedData) {
                    // Handle Laravel paginator format
                    setSlashNotes(paginatedData.data || []);
                    setPagination(prev => ({
                        ...prev,
                        totalRecords: paginatedData.total || 0,
                        lastPage: paginatedData.last_page || 1,
                        from: paginatedData.from || 0,
                        to: paginatedData.to || 0,
                        current_page: paginatedData.current_page || 1
                    }));
                } else if (Array.isArray(response)) {
                    // Fallback for non-paginated array response
                    setSlashNotes(response);
                    setPagination(prev => ({
                        ...prev,
                        totalRecords: response.length,
                        lastPage: 1,
                        from: 1,
                        to: response.length
                    }));
                } else if (response.data && Array.isArray(response.data)) {
                    // Fallback for simple data array
                    setSlashNotes(response.data);
                    setPagination(prev => ({
                        ...prev,
                        totalRecords: response.data.length,
                        lastPage: 1,
                        from: 1,
                        to: response.data.length
                    }));
                } else {
                    setSlashNotes([]);
                }
                console.log('Fetched slash notes:', response); 
            }
        } catch (error) {
            console.error('Error fetching slash notes:', error);
            const { Toast } = props;
            if (Toast) {
                Toast.error("Failed to fetch slash notes");
            }
        } finally {
            Loader(false);
        }
    };

    const handlePaginationChange = (newPagination) => {
        setPagination(prev => ({
            ...prev,
            page: newPagination.page || prev.page,
            perPage: newPagination.perPage || prev.perPage
        }));
    };

    const handleIconClick = () => {
        const today = dayjs();
        const formattedDate = today.format('MM/DD/YYYY');
        setNoteDate(formattedDate);
        setNoteText("");
        setEditingNoteId(null);
        setIsEditMode(false);
        setIsModalOpen(true);
    };

    const handleEditClick = (noteId) => {
        const noteToEdit = slashNotes.find(note => note.slash_note_id === noteId);
        if (noteToEdit) {
            setNoteDate(noteToEdit.note_date || "");
            setNoteText(noteToEdit.note_text || "");
            setEditingNoteId(noteId);
            setIsEditMode(true);
            setIsModalOpen(true);
        }
    };

    const handleDelete = async (noteId) => {
        const { Toast } = props;
        if (!noteId) {
            if (Toast) {
                Toast.error("Note ID is missing");
            }
            return;
        }
        try {
            const response = await EmployeeService.deleteSlashNote(noteId);
            if (response && (response.code === 200 || response.code === 204 || response.status === "SUCCESS")) {
                if (Toast) {
                    Toast.success("Discussion Note deleted successfully");
                }
                fetchSlashNotes();
            }
            // Check if response is an error object 
            else if (response && (response.code === 404 || response.status === "ERROR")) {
                const errorMessage = response.data?.message || response.message || "Slash note not found";
                if (Toast) {
                    Toast.error(errorMessage);
                }
            }
            // Default: assume success if we gt here
            else {
                if (Toast) {
                    Toast.success("Discussion Note deleted successfully");
                }
                fetchSlashNotes();
            }
        } catch (error) {
            console.error('Error deleting discussion note:', error);
            const errorMessage = error?.response?.data?.message || error?.data?.message || error?.message || "Failed to delete slash note";
            if (Toast) {
                Toast.error(errorMessage);
            }
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setNoteDate("");
        setNoteText("");
        setDateError("");
        setTextError("");
        setEditingNoteId(null);
        setIsEditMode(false);
    };
    const handleDateChange = (val) => {
        setNoteDate(val);
        if (val) {
            setDateError("");
        }
    };
    const handleTextChange = (val) => {
        setNoteText(val);
        if (val) {
            setTextError("");
        }
    };
    const handleSubmit = async () => {
        const { Toast } = props;
        let hasError = false;
        if (!noteDate) {
            setDateError("Date is required");
            hasError = true;
        }
        if (!noteText || noteText.trim() === "") {
            setTextError("Discussion Note is required");
            hasError = true;
        }
        if (hasError) {
            return;
        }
        if (!patientDetails?.employee_id) {
            if (Toast) {
                Toast.error("Employee ID is missing");
            }
            return;
        }
        setIsSaving(true);
        try {
            // Validate and ensure date is in MM/DD/YYYY format
            if (!noteDate) {
                setDateError("Date is required");
                setIsSaving(false);
                return;
            }
            // Ensure date is in MM/DD/YYYY format
            let formattedDate = noteDate;
            if (!noteDate.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
                // Try to parse and reformat if needed
                const parsedDate = dayjs(noteDate);
                if (parsedDate.isValid()) {
                    formattedDate = parsedDate.format('MM/DD/YYYY');
                } else {
                    setDateError("Please enter a valid date in MM/DD/YYYY format");
                    setIsSaving(false);
                    return;
                }
            }
            const noteData = {
                employee_id: patientDetails.employee_id,
                note_date: formattedDate,
                note_text: noteText.trim(),
                visit_id: patientDetails.visit_id || null
            };
            
            let response;
            if (isEditMode && editingNoteId) {
                // Update existing note
                response = await EmployeeService.updateSlashNote(editingNoteId, noteData);
                console.log('Update response:', response);
            } else {
                // Create new note
                response = await EmployeeService.createSlashNote(noteData);
                console.log('Create response:', response); 
            }
            if (response) {
                if (Toast) {
                    Toast.success(isEditMode ? "Discussion Note updated successfully" : "Discussion Note created successfully");
                }
                handleCloseModal();
                fetchSlashNotes(); 
            } else {
                if (Toast) {
                    Toast.error(isEditMode ? "Failed to update discussion note" : "Failed to create discussion note");
                }
            }
        } catch (error) {
            console.error(`Error ${isEditMode ? 'updating' : 'creating'} slash note:`, error);
            if (Toast) {
                Toast.error(isEditMode ? "Failed to update discussion note" : "Failed to create discussion note");
            }
        } finally {
            setIsSaving(false);
        }
    };
    const formatTableRows = () => {
        if (!slashNotes || slashNotes.length === 0) {
            return [];
        }
        return slashNotes.map((note, index) => ({
            rowId: note.slash_note_id || `note-${index}`,
            data: {
                note_date: note.note_date || "-",
                note_text: note.note_text || "-",
                actions: [
                    {
                        className: "btn btn-icon btn-transparent-dark",
                        iconType: "Search",
                        title: "Update",
                        clickHandler: () => handleEditClick(note.slash_note_id),
                    },
                    {
                        className: "btn btn-icon btn-transparent-dark",
                        iconType: "Remove",
                        title: "Delete",
                        isDelete: true,
                        acl: "any",
                        clickHandler: () => handleDelete(note.slash_note_id),
                    }
                ]
            }
        }));
    };

    const tableRecords = {
        tableHeaders: tableHeaders,
        tableRows: {
            data: formatTableRows()
        }
    };

    return(
        <>
          <div className="w-100 p-3">
            <div className="sh_cardBox h-auto shadow-none border">
            <div className="sh_cardHeader">
                <div className="form-group d-flex mb-0 justify-content-between align-items-center">
                <h6 className="text-sm mb-0">{selectedTab} - {patientDetails?.patientName}</h6>
                <span
                    style={{ cursor: "pointer"}}
                    onClick={handleIconClick}
                    title="Add Slash Note"
                >
                    <Icons type="AddIconDetailView"  />
                </span>
                </div>
            </div>
            <div className="sh_cardBody p-0" style={{ maxHeight: 'calc(-319px + 85vh)', overflowY: 'auto', position: 'relative' }}>
                <TableGrid 
                hasPagination={true}
                pagination={pagination}
                fetchTableRecords={handlePaginationChange}
                {...tableRecords} 
                />
            </div>
            </div>
        </div>

        {/* Slash Note Modal */}
        <Modal
            show={isModalOpen}
            onHide={handleCloseModal}
            dialogClassName=""
            keyboard={false}
            contentClassName="modal-dialog hResponsive"
            backdrop="static"
            size="md"
        >
            <div className="modal-content">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <h6 className="font-weight-bold text-lg mb-0">{isEditMode ? "Edit Discussion Note" : "Add Discussion Note"}</h6>
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <div className="row">
                        <div className="col-12 mb-3">
                            <div className="form-group">
                                <label className="mb-1 text-md opacity-75">Discussion Date:</label>
                                <FormInputs
                                    type="Date"
                                    changeHandler={handleDateChange}
                                    value={noteDate}
                                />
                                {dateError && (
                                    <div className="invalid-feedback d-block">{dateError}</div>
                                )}
                            </div>
                        </div>
                        <div className="col-12">
                            <div className="form-group">
                                <label className="mb-1 text-md opacity-75">Discussion Notes:</label><span className="text-danger">*</span>
                                <FormInputs
                                    type="TextArea"
                                    changeHandler={handleTextChange}
                                    value={noteText}
                                    placeholder="Enter discussion notes"
                                    rows="5"
                                    required={true}
                                />
                                {textError && (
                                    <div className="text-danger text-sm">{textError}</div>
                                )}
                            </div>
                        </div>
                    </div>
                </Modal.Body>

                <Modal.Footer className="justify-content-center modal-footer">
                    <Button
                        variant="secondary"
                        className="btn btn-secondary p-2"
                        onClick={handleCloseModal}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        className="btn btn-primary"
                        onClick={handleSubmit}
                        disabled={isSaving}
                    >
                        {isSaving ? (isEditMode ? "Updating..." : "Saving...") : (isEditMode ? "Update" : "Save")}
                    </Button>
                </Modal.Footer>
            </div>
        </Modal>
        </>
    )
}

export default WithReduxState(WithRoutify(SlashNote));